'use client';

import { useState, useRef, useEffect } from 'react';
import { Search, X, UserCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { useCustomerSuggestions } from './use-customer-suggestions';

interface CustomerFilterProps {
  /**
   * Visuelle Dichte: `'sm'` (Default) für das kompakte Desktop-Popover,
   * `'md'` für größere Touch-Targets im Mobile-BottomSheet.
   */
  size?: 'sm' | 'md';
}

/**
 * Admin-only customer filter shown below the "Alle Kunden" toggle.
 * Loads unique customers from documents and provides autocomplete search.
 */
export function CustomerFilter({ size = 'sm' }: CustomerFilterProps) {
  const isMd = size === 'md';
  const {
    selectedCustomers,
    selectedCustomerNumbers,
    addCustomer,
    removeCustomer,
    clearCustomers,
  } = useAdmin();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { customers, suggestions } = useCustomerSuggestions(query);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (customerNumber: string) => {
    const entry = customers.find((c) => c.customerNumber === customerNumber);
    addCustomer({
      customerNumber,
      customerName: entry?.customerName || customerNumber,
    });
    setQuery('');
    setIsOpen(false);
  };

  const handleRemove = (customerNumber: string) => {
    removeCustomer(customerNumber);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && query.trim()) {
      // Free text entry: treat input as customer number
      const trimmed = query.trim().toUpperCase();
      handleSelect(trimmed);
    }
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Chips + dropdown-highlight color follow the banner mode:
  // single debitor → red (cross-login), multiple → amber (warning).
  const isMulti = selectedCustomers.length >= 2;
  const chipClass = isMulti
    ? 'bg-amber-100 text-amber-800'
    : 'bg-red-100 text-red-700';
  const chipHover = isMulti ? 'hover:text-amber-900' : 'hover:text-red-900';
  const suggestionSelectedClass = isMulti
    ? 'bg-amber-50 text-amber-800'
    : 'bg-red-50 text-red-700';

  return (
    <div ref={containerRef} className="mt-2 space-y-2">
      {/* Selected chips + Alle entfernen */}
      {selectedCustomers.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedCustomers.map((c) => (
            <span
              key={c.customerNumber}
              className={clsx(
                'inline-flex items-center gap-1.5 font-medium rounded-full',
                isMd ? 'pl-3 pr-2 py-1.5 text-sm' : 'pl-2.5 pr-1.5 py-1 text-xs',
                chipClass,
              )}
            >
              <span className={clsx('truncate', isMd ? 'max-w-[180px]' : 'max-w-[140px]')}>
                {c.customerName}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(c.customerNumber)}
                aria-label={`${c.customerName} entfernen`}
                className={`${chipHover} hover:bg-black/5 rounded-full p-0.5 transition-colors`}
              >
                <X size={isMd ? 14 : 12} />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearCustomers}
            className={clsx(
              'text-zinc-500 hover:text-zinc-700 font-medium rounded-full border border-dashed border-zinc-300 hover:border-zinc-400 transition-colors',
              isMd ? 'text-sm px-3 py-1.5' : 'text-xs px-2.5 py-1',
            )}
          >
            Alle entfernen
          </button>
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search
          size={14}
          className={clsx(
            'absolute top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none',
            isMd ? 'left-3' : 'left-2.5',
          )}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Debitor suchen..."
          className={clsx(
            'w-full pr-3 bg-white border border-zinc-200 rounded-lg outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 placeholder:text-zinc-400',
            isMd ? 'pl-9 py-2.5 text-sm' : 'pl-8 py-1.5 text-xs',
          )}
        />
      </div>

      {/* Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {suggestions.slice(0, 50).map((c) => {
            const isSelected = selectedCustomerNumbers.includes(c.customerNumber);
            return (
              <button
                key={c.customerNumber}
                onClick={() => handleSelect(c.customerNumber)}
                className={clsx(
                  'w-full text-left px-3 hover:bg-zinc-50 transition-colors flex items-center gap-2 border-b border-zinc-50 last:border-0',
                  isMd ? 'py-2.5 text-sm' : 'py-2 text-xs',
                  isSelected ? suggestionSelectedClass : 'text-zinc-700',
                )}
              >
                <UserCircle size={14} className="shrink-0 text-zinc-400" />
                <div className="min-w-0">
                  <span className="font-medium block truncate">
                    {c.customerName}
                  </span>
                  <span className="text-zinc-400 font-mono text-[10px]">
                    {c.customerNumber}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {isOpen && query.trim() && suggestions.length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg shadow-lg p-3 text-xs text-zinc-500 text-center">
          Kein Debitor gefunden.
          <br />
          <span className="text-zinc-400">
            Enter drücken für &quot;{query.trim().toUpperCase()}&quot;
          </span>
        </div>
      )}
    </div>
  );
}
